import React, { useState } from "react";
import { ChevronDown, ChevronRight, Package, Search } from "lucide-react";

import { Input } from "@/components/ui/input";

import { formatCurrency, formatQtd } from "../lib/formato";
import DetalheCanais from "./DetalheCanais";

export const ordenar = (lista, { key, dir }) =>
  [...(lista || [])].sort((a, b) => ((a[key] || 0) - (b[key] || 0)) * (dir === "desc" ? -1 : 1));

const corMargem = (m) => (m >= 50 ? ["text-emerald-600 dark:text-emerald-400", "bg-emerald-500"]
  : m >= 30 ? ["text-green-600 dark:text-green-400", "bg-green-500"]
    : m >= 15 ? ["text-amber-600 dark:text-amber-400", "bg-amber-500"] : ["text-red-600 dark:text-red-400", "bg-red-500"]);

const COLUNAS = [["quantidade", "Qtd"], ["valor_total", "Faturamento"], ["cmv_total", "CMV"], ["lucro", "Lucro"], ["margem_pct", "Margem"]];

function Miniatura({ p }) {
  return (
    <div className="w-9 h-9 rounded-lg bg-muted/60 overflow-hidden flex-shrink-0 border border-border/50">
      {p.photo_url ? (
        <img src={p.photo_url.startsWith("http") ? p.photo_url : `/api${p.photo_url}`} alt={p.nome} className="w-full h-full object-cover" loading="lazy"
          onError={(e) => { e.target.style.display = "none"; if (e.target.nextSibling) e.target.nextSibling.style.display = "flex"; }} />
      ) : null}
      <div className={`${p.photo_url ? "hidden" : "flex"} w-full h-full items-center justify-center`}><Package className="w-4 h-4 text-muted-foreground/40" /></div>
    </div>
  );
}

function Linha({ p, i, aberto, alternar }) {
  const cmv = p.cmv_total || 0;
  const lucro = p.lucro != null ? p.lucro : p.valor_total - cmv;
  const margem = p.margem_pct != null ? p.margem_pct : (p.valor_total > 0 ? (lucro / p.valor_total) * 100 : 0);
  const [corTexto, corBarra] = corMargem(margem);
  return (
    <>
      <tr className={`border-t hover:bg-muted/30 cursor-pointer transition-colors ${aberto ? "bg-muted/30" : ""}`} onClick={alternar}>
        <td className="pl-3 w-8">{aberto ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}</td>
        <td className="py-2 px-2 text-muted-foreground tabular-nums">{i + 1}</td>
        <td className="py-2 px-2 font-medium">
          <div className="flex items-center gap-2.5">
            <Miniatura p={p} />
            <div className="flex items-center gap-2 min-w-0">
              <span className="truncate max-w-[220px]">{p.nome}</span>
              {p.categoria && <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 font-semibold uppercase tracking-wide flex-shrink-0 border border-orange-500/30">{p.categoria}</span>}
            </div>
          </div>
        </td>
        <td className="py-2 px-2 text-right font-mono font-semibold tabular-nums whitespace-nowrap">{formatQtd(p.quantidade, p.unidade)}</td>
        <td className="py-2 px-2 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">{formatCurrency(p.valor_total)}</td>
        <td className="py-2 px-2 text-right font-mono text-red-600 dark:text-red-400 tabular-nums">{cmv > 0 ? formatCurrency(cmv) : "—"}</td>
        <td className={`py-2 px-2 text-right font-mono font-semibold tabular-nums ${lucro >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>{cmv > 0 ? formatCurrency(lucro) : "—"}</td>
        <td className="py-2 px-2 pr-5 text-right">
          {cmv > 0 ? (
            <div className="inline-flex items-center gap-2">
              <div className="w-14 h-1.5 bg-muted rounded-full overflow-hidden"><div className={`h-full rounded-full ${corBarra}`} style={{ width: `${Math.min(Math.max(margem, 0), 100)}%` }} /></div>
              <span className={`font-mono font-bold text-xs min-w-[48px] tabular-nums ${corTexto}`}>{margem.toFixed(1).replace(".", ",")}%</span>
            </div>
          ) : <span className="text-muted-foreground text-xs">—</span>}
        </td>
      </tr>
      {aberto && <tr className="border-t bg-muted/10"><td colSpan={8} className="py-5 px-5"><DetalheCanais p={p} cmv={cmv} /></td></tr>}
    </>
  );
}

// "Todos os Produtos": busca, ordenação por coluna e linha que abre a venda por canal.
export default function TabelaProdutos({ produtos, sort, setSort }) {
  const [aberto, setAberto] = useState(null);
  const [busca, setBusca] = useState("");
  const termo = busca.toLowerCase().trim();
  const ordenados = ordenar(produtos, sort);
  const lista = termo ? ordenados.filter((p) => (p.nome || "").toLowerCase().includes(termo)) : ordenados;
  const clicar = (key) => setSort((prev) => ({ key, dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc" }));
  return (
    <div className="bg-card rounded-xl border overflow-hidden">
      <div className="px-5 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-bold">Todos os Produtos ({produtos.length})</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto..." className="pl-9 h-9 w-64" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="w-8"></th>
              <th className="text-left py-2.5 px-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">#</th>
              <th className="text-left py-2.5 px-2 text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Produto</th>
              {COLUNAS.map(([key, rotulo], i) => (
                <th key={key} onClick={() => clicar(key)}
                  className={`text-right py-2.5 px-2 ${i === COLUNAS.length - 1 ? "pr-5" : ""} text-[11px] uppercase tracking-wider text-muted-foreground font-semibold cursor-pointer select-none`}>
                  {rotulo}{sort.key === key && <span className="ml-1">{sort.dir === "desc" ? "▼" : "▲"}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lista.length === 0
              ? <tr><td colSpan={8} className="py-10 text-center text-muted-foreground text-xs">Nenhum produto bate com "{busca}"</td></tr>
              : lista.map((p, i) => {
                // id + nome: o servidor pode devolver o MESMO produto em duas linhas
                // com nomes diferentes (ex.: "Atum Especial" e "1 Atum Especial");
                // só o id repetia a chave e abrir uma linha abria as duas.
                const key = `${p.id || ""}|${p.nome}`;
                return <Linha key={key} p={p} i={i} aberto={aberto === key} alternar={() => setAberto(aberto === key ? null : key)} />;
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
